// ABOUTME: Table displaying all products with their resolved config values and level badges.
// ABOUTME: Supports search, sorting, and click-to-edit with visual change tracking.

import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { colors } from '../../theme/colors';
import { formatHorizon } from '../../utils/formatters';
import LevelBadge from './LevelBadge';
import useSortableTable from '../../hooks/useSortableTable';

const FIELD_COLUMNS = [
  { key: 'batch_size', width: '0.6fr' },
  { key: 'preparation_minutes', width: '0.6fr' },
  { key: 'baking_minutes', width: '0.6fr' },
  { key: 'unloading_minutes', width: '0.7fr' },
  { key: 'opening_time', width: '0.6fr' },
  { key: 'closing_time', width: '0.6fr' },
  { key: 'closing_minutes_before_shop', width: '0.7fr' },
  { key: 'prediction_horizon_minutes', width: '0.7fr' },
  { key: 'ignore', width: '0.5fr' },
];

const GRID_TEMPLATE = '0.4fr 2fr 1fr ' + FIELD_COLUMNS.map(c => c.width).join(' ');

function getCellStyle(productId, fieldName, pendingChanges, affectedCells) {
  // Check if this cell has a direct pending change
  const isPending = pendingChanges.some(
    c => String(c.entityId) === String(productId) && c.field === fieldName
  );
  if (isPending) {
    return {
      backgroundColor: '#FFF7ED',
      borderLeft: `3px solid ${colors.palette.orange}`,
    };
  }

  // Check if this cell is cascade-affected
  const isAffected = affectedCells.some(
    a => a.product_id === productId && a.field === fieldName
  );
  if (isAffected) {
    return {
      backgroundColor: '#EFF6FF',
    };
  }

  return {};
}

function formatValue(value, fieldName, units) {
  if (value === null || value === undefined) return '-';
  if (fieldName === 'ignore') return value ? 'Yes' : 'No';
  if (fieldName === 'prediction_horizon_minutes') return formatHorizon(value, units);
  return String(value);
}

function SortableHeader({ field, label, sortField, sortDirection, onSort }) {
  const isActive = sortField === field;
  return (
    <Box
      onClick={() => onSort(field)}
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        '&:hover': { color: colors.text.primary },
      }}
    >
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.text.secondary }}>
        {label}
      </Typography>
      {isActive && (
        <Typography sx={{ fontSize: '0.65rem', color: colors.text.muted }}>
          {sortDirection === 'asc' ? '▲' : '▼'}
        </Typography>
      )}
    </Box>
  );
}

export default function ProductConfigTable({
  products,
  categories,
  searchQuery,
  categoryFilter,
  pendingChanges,
  affectedCells,
  onCellClick,
  onDeleteProduct,
}) {
  const { t } = useTranslation();
  const horizonUnits = { hourUnit: t('common.hours'), minuteUnit: t('common.minutes') };
  const gridTemplate = GRID_TEMPLATE + (onDeleteProduct ? ' 2.5rem' : '');

  // Filter products
  const filteredProducts = useMemo(() => {
    let result = products || [];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        String(p.product_id).includes(q)
      );
    }
    if (categoryFilter && categoryFilter !== 'all') {
      result = result.filter(p => p.category === categoryFilter);
    }
    return result;
  }, [products, searchQuery, categoryFilter]);

  // Sorting - flatten fields.X.value to top-level for hook compatibility
  const flattenedProducts = useMemo(() => {
    return filteredProducts.map(p => {
      const flat = { ...p };
      for (const col of FIELD_COLUMNS) {
        flat[col.key] = p.fields?.[col.key]?.value;
      }
      return flat;
    });
  }, [filteredProducts]);

  const { sortedItems, sortKey, sortDirection, handleSort } = useSortableTable(
    flattenedProducts,
    { defaultSortKey: 'product_id', defaultSortDirection: 'asc' }
  );

  const getCategoryColor = (categoryId) => {
    const cat = (categories || []).find(c => c.id === categoryId);
    return cat?.color || colors.category.default;
  };

  return (
    <Box>
      {/* Header row */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: gridTemplate,
        gap: 1,
        px: 1.5,
        py: 1,
        borderBottom: `2px solid ${colors.border.light}`,
        position: 'sticky',
        top: 0,
        backgroundColor: colors.background.white,
        zIndex: 1,
      }}>
        <SortableHeader field="product_id" label="ID" sortField={sortKey} sortDirection={sortDirection} onSort={handleSort} />
        <SortableHeader field="name" label={t('config.fields.name')} sortField={sortKey} sortDirection={sortDirection} onSort={handleSort} />
        <SortableHeader field="category" label={t('config.fields.category')} sortField={sortKey} sortDirection={sortDirection} onSort={handleSort} />
        {FIELD_COLUMNS.map(col => (
          <SortableHeader
            key={col.key}
            field={col.key}
            label={t(`config.fields.${col.key}`)}
            sortField={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        ))}
        {onDeleteProduct && <Box />}
      </Box>

      {/* Data rows */}
      {sortedItems.map(product => {
        const pendingNameChange = pendingChanges.find(
          c => String(c.entityId) === String(product.product_id) && c.field === 'name'
        );
        const nameCellStyle = getCellStyle(product.product_id, 'name', pendingChanges, affectedCells);
        const displayName = pendingNameChange ? pendingNameChange.value : product.name;

        const pendingCatChange = pendingChanges.find(
          c => String(c.entityId) === String(product.product_id) && c.field === 'category'
        );
        const catCellStyle = getCellStyle(product.product_id, 'category', pendingChanges, affectedCells);
        const displayCategory = pendingCatChange ? pendingCatChange.value : product.category;

        return (
        <Box
          key={product.product_id}
          sx={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderBottom: `1px solid ${colors.border.lighter}`,
            '&:hover': { backgroundColor: colors.background.hover },
            alignItems: 'center',
          }}
        >
          {/* ID */}
          <Typography sx={{ fontSize: '0.75rem', color: colors.text.muted }}>
            {product.product_id}
          </Typography>

          {/* Name */}
          <Box
            onClick={() => onCellClick(product, 'name', {
              value: product.name,
              level: product.name_level,
              source: product.name_source,
            })}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              px: 0.5,
              py: 0.25,
              borderRadius: '4px',
              minWidth: 0,
              overflow: 'hidden',
              ...nameCellStyle,
              '&:hover': {
                backgroundColor: nameCellStyle.backgroundColor || '#f0f0f0',
                outline: `1px solid ${colors.border.light}`,
              },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Typography sx={{
                fontSize: '0.8rem',
                fontWeight: pendingNameChange ? 600 : 500,
                color: pendingNameChange ? colors.palette.orange : colors.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {displayName || '-'}
              </Typography>
              {product.db_name && (
                <Typography sx={{
                  fontSize: '0.7rem',
                  color: colors.status.warning,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {t('warnings.dbNameMismatch', { name: product.db_name })}
                </Typography>
              )}
            </Box>
            {(pendingNameChange || product.name_level) && (
              <LevelBadge level={pendingNameChange ? pendingNameChange.level : product.name_level} />
            )}
          </Box>

          {/* Category */}
          <Box
            onClick={() => onCellClick(product, 'category', {
              value: product.category,
              level: product.category_level,
              source: product.category_source,
            })}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.5,
              py: 0.25,
              borderRadius: '4px',
              minWidth: 0,
              overflow: 'hidden',
              ...catCellStyle,
              '&:hover': {
                backgroundColor: catCellStyle.backgroundColor || '#f0f0f0',
                outline: `1px solid ${colors.border.light}`,
              },
            }}
          >
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: getCategoryColor(displayCategory),
              flexShrink: 0,
            }} />
            <Typography sx={{
              fontSize: '0.75rem',
              color: pendingCatChange ? colors.palette.orange : colors.text.secondary,
              fontWeight: pendingCatChange ? 600 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}>
              {displayCategory || '-'}
            </Typography>
            {(pendingCatChange || product.category_level) && (
              <LevelBadge level={pendingCatChange ? pendingCatChange.level : product.category_level} />
            )}
          </Box>

          {/* Field columns */}
          {FIELD_COLUMNS.map(col => {
            const fieldData = product.fields?.[col.key] || {};
            const cellStyle = getCellStyle(
              product.product_id, col.key, pendingChanges, affectedCells
            );
            const pendingChange = pendingChanges.find(
              c => String(c.entityId) === String(product.product_id) && c.field === col.key
            );
            const affectedCell = !pendingChange && affectedCells.find(
              a => a.product_id === product.product_id && a.field === col.key
            );
            const isChanged = !!pendingChange || !!affectedCell;
            const displayValue = pendingChange ? pendingChange.value
              : affectedCell ? affectedCell.new_value
              : fieldData.value;
            return (
              <Box
                key={col.key}
                onClick={() => onCellClick(product, col.key, fieldData)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: '4px',
                  ...cellStyle,
                  '&:hover': {
                    backgroundColor: cellStyle.backgroundColor || '#f0f0f0',
                    outline: `1px solid ${colors.border.light}`,
                  },
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: isChanged ? colors.palette.orange : colors.text.primary, fontWeight: isChanged ? 600 : 400 }}>
                  {formatValue(displayValue, col.key, horizonUnits)}
                </Typography>
                <LevelBadge level={pendingChange ? pendingChange.level : fieldData.level} />
              </Box>
            );
          })}

          {/* Delete button */}
          {onDeleteProduct && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onDeleteProduct(product); }}
                sx={{
                  color: colors.text.muted,
                  '&:hover': { color: colors.status.error },
                  padding: '2px',
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>
        );
      })}

      {sortedItems.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ color: colors.text.muted, fontSize: '0.9rem' }}>
            {t('config.noResults')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
